import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Signature as SigIcon, Plus, Trash2, Sparkles, Bot, Code2, Eye } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

export default function Signatures() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("visual"); // visual | html
  const empty = { company_id: "", account_id: "", name: "", template_key: "custom", html: "", is_default_user: false, is_default_ai: false };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [s, c, a, t] = await Promise.all([
      api.get("/signatures", { params }),
      api.get("/companies"),
      api.get("/email-accounts", { params }),
      api.get("/signatures/templates"),
    ]);
    setItems(s.data); setCompanies(c.data); setAccounts(a.data); setTemplates(t.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const applyTemplate = (key) => {
    const t = templates.find((x) => x.key === key);
    setForm((f) => ({ ...f, template_key: key, html: t ? t.html : f.html, name: f.name || (t?.name || "") }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/signatures", { ...form, account_id: form.account_id || null });
      toast.success("Signature saved");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete signature?")) return;
    await api.delete(`/signatures/${id}`);
    toast.success("Deleted"); load();
  };

  const toggleFlag = async (s, key) => {
    await api.patch(`/signatures/${s.id}`, { [key]: !s[key] });
    load();
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="signatures-page">
      <PageHeader
        title="Email signatures"
        subtitle="HTML signatures with company logo, per-mailbox defaults, and an AI-agent default for automated replies."
        eyebrow="Branding"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl shadow-md shadow-primary/20" data-testid="new-signature-button">
                <Plus className="h-4 w-4 mr-1.5" /> New signature
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-4xl">
              <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">Create signature</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
                <div className="space-y-3">
                  <F label="Company">
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Mailbox (optional)">
                    <Select value={form.account_id || ""} onValueChange={(v) => setForm({ ...form, account_id: v || "" })}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Shared" /></SelectTrigger>
                      <SelectContent>
                        {accounts.filter((a) => a.company_id === form.company_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.email_address}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" data-testid="sig-name-input" /></F>
                  <F label="Template">
                    <Select value={form.template_key} onValueChange={applyTemplate}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom</SelectItem>
                        {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Design</Label>
                      <div className="inline-flex rounded-lg border border-border overflow-hidden text-[10px] font-mono uppercase" data-testid="sig-mode-switch">
                        <button type="button" onClick={() => setMode("visual")} className={`px-2 py-1 flex items-center gap-1 ${mode === "visual" ? "bg-emerald-brand/15 text-emerald-brand" : "text-muted-foreground hover:bg-muted"}`} data-testid="sig-mode-visual"><Eye className="h-3 w-3" /> Visual</button>
                        <button type="button" onClick={() => setMode("html")} className={`px-2 py-1 flex items-center gap-1 ${mode === "html" ? "bg-emerald-brand/15 text-emerald-brand" : "text-muted-foreground hover:bg-muted"}`} data-testid="sig-mode-html"><Code2 className="h-3 w-3" /> HTML</button>
                      </div>
                    </div>
                    {mode === "visual" ? (
                      <div data-testid="sig-visual-editor">
                        <RichTextEditor value={form.html} onChange={(v) => setForm({ ...form, html: v })} placeholder="Design your signature — logo (via image button), name, title, links…" />
                      </div>
                    ) : (
                      <Textarea rows={10} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} className="rounded-xl font-mono text-xs" data-testid="sig-html-input" />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Toggle label="Default for me" checked={form.is_default_user} onChange={(v) => setForm({ ...form, is_default_user: v })} testid="sig-default-user" />
                    <Toggle label="Default for AI agent" checked={form.is_default_ai} onChange={(v) => setForm({ ...form, is_default_ai: v })} testid="sig-default-ai" />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Live preview</div>
                  <div className="border border-border rounded-xl p-4 bg-white min-h-[300px]" dangerouslySetInnerHTML={{ __html: previewFill(form.html) }} />
                  <p className="text-[11px] text-muted-foreground mt-2">Variables: <code>{"{{full_name}} {{email}} {{phone}} {{company}} {{title}} {{website}} {{logo_url}}"}</code></p>
                </div>
                <DialogFooter className="md:col-span-2"><Button type="submit" className="rounded-xl" data-testid="sig-create-submit">Save signature</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((s) => (
          <div key={s.id} className="card-3d p-5" data-testid={`sig-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-emerald-brand/12 text-emerald-brand grid place-items-center"><SigIcon className="h-4 w-4" /></div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{s.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate">
                    {s.template_key} · {s.account_id ? "mailbox-scoped" : "shared"}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`sig-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="mt-3 border border-border rounded-lg p-3 bg-white max-h-32 overflow-hidden text-xs" dangerouslySetInnerHTML={{ __html: previewFill(s.html) }} />
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={() => toggleFlag(s, "is_default_user")} className={`pill text-[10px] font-mono uppercase ${s.is_default_user ? "bg-emerald-brand/15 text-emerald-brand" : "bg-muted text-muted-foreground hover:bg-muted/70"}`} data-testid={`sig-toggle-user-${s.id}`}>
                <Sparkles className="h-3 w-3 inline mr-1 -mt-0.5" /> {s.is_default_user ? "My default" : "Set as my default"}
              </button>
              <button onClick={() => toggleFlag(s, "is_default_ai")} className={`pill text-[10px] font-mono uppercase ${s.is_default_ai ? "bg-sun/25 text-yellow-800" : "bg-muted text-muted-foreground hover:bg-muted/70"}`} data-testid={`sig-toggle-ai-${s.id}`}>
                <Bot className="h-3 w-3 inline mr-1 -mt-0.5" /> {s.is_default_ai ? "AI default" : "Set as AI default"}
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-muted-foreground text-sm col-span-2 text-center py-10">No signatures yet. Click <b>New signature</b> to start from a template.</div>}
      </div>
    </div>
  );
}

function previewFill(html) {
  const vars = {
    full_name: "Jane Doe",
    email: "jane@example.com",
    phone: "+1 555 0100",
    company: "Advanced Network Communications",
    title: "Account Manager",
    website: "advancednetcom.lat",
    logo_url: "https://api.dicebear.com/9.x/shapes/svg?seed=relay",
  };
  return (html || "").replace(/\{\{\s*([\w_]+)\s*\}\}/g, (_, k) => vars[k.trim()] ?? "");
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
      <span className="text-xs">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
